/**
 * Newsletter / Lead Validation Schemas
 *
 * Shared by the client (newsletter-cta-section) and the server route
 * (POST /api/leads) so a payload that validates in the browser is the exact
 * shape the API accepts — one source of truth, no drift between the two.
 */

import { z } from 'zod';
import type { NextRequest } from 'next/server';
import { SERVICE_TYPES } from './contact-form';
import type { CreateLeadInput } from '@/lib/domain/lead.types';
import { LOCALE_COOKIE_NAME, isSupportedLocale } from '@/i18n/locales';

/**
 * Every rule carries an i18n key SUFFIX (never user-facing copy), so the UI maps
 * it to `errors.<suffix>`. required_error / invalid_type_error cover a missing or
 * non-string email, and min/max/email each carry a key too, so EVERY failure
 * emits a suffix from NEWSLETTER_ERROR_KEYS — a Zod default can never reach the
 * visitor as a raw key path. max(254) mirrors the contact schema's cap.
 */
export const newsletterLeadSchema = z.object({
  email: z
    .string({ required_error: 'emailInvalid', invalid_type_error: 'emailInvalid' })
    .trim()
    .min(1, 'emailInvalid')
    .max(254, 'emailTooLong')
    .email('emailInvalid'),
});

export type NewsletterLeadInput = z.infer<typeof newsletterLeadSchema>;

/**
 * Every error-key suffix this schema can emit. The i18n key-coverage test
 * asserts both locale bundles answer each one, so a rename fails a test instead
 * of reaching a visitor as a raw key path. Mirrors CONTACT_ERROR_KEYS.
 */
export const NEWSLETTER_ERROR_KEYS = ['emailInvalid', 'emailTooLong'] as const;
export type NewsletterErrorKey = (typeof NEWSLETTER_ERROR_KEYS)[number];

/** Guard so the renderer only ever interpolates a known key into a translation. */
export function isNewsletterErrorKey(key: string): key is NewsletterErrorKey {
  return (NEWSLETTER_ERROR_KEYS as readonly string[]).includes(key);
}

/**
 * Validates the client-controlled `m40_attr` cookie. Every field is an optional,
 * length-capped string; a malformed or hostile value is coerced away (`.catch`)
 * rather than crashing the insert. There is no passthrough, so unknown keys are
 * dropped — the schema itself is the allow-list, and the cookie can never write
 * an unbounded blob into the Lead columns.
 */
const cappedString = z.string().max(512).optional().catch(undefined);

export const m40AttrCookieSchema = z.object({
  gclid: cappedString,
  // iOS/app-privacy click IDs Google sends instead of gclid when gclid can't be
  // set (SKAdNetwork / ITP). Capturing them is what keeps iOS ad clicks from
  // becoming unattributable. They have no dedicated Lead column — the route
  // routes them into the Lead.attribution JSON via attributionExtra. (WSP-834)
  gbraid: cappedString,
  wbraid: cappedString,
  utmSource: cappedString,
  utmMedium: cappedString,
  utmCampaign: cappedString,
  utmTerm: cappedString,
  utmContent: cappedString,
  referrer: cappedString,
  landingPage: cappedString,
});

/**
 * Server-side validation for POST /api/contact (WSP-836). Mirrors the wire
 * shape contact-form.tsx actually posts (not the client-only ContactFormValues
 * shape, which carries `variant` instead of `isJobApplication`). Messages are
 * plain English, matching pages/api/contact.ts's existing 400 responses —
 * unlike newsletterLeadSchema, this payload never reaches a translated UI.
 *
 * serviceType reuses SERVICE_TYPES from contact-form.ts so the allow-list has
 * one source of truth instead of drifting between client and server.
 */
export const contactLeadSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required.').max(80, 'First name is too long.'),
    lastName: z.string().trim().max(80, 'Last name is too long.').optional(),
    email: z
      .string()
      .trim()
      .min(1, 'Email is required.')
      .max(254, 'Email is too long.')
      .email('A valid email is required.'),
    phoneNumber: z.string().trim().max(20, 'Phone number is too long.').optional(),
    message: z.string().trim().min(1, 'Message is required.').max(2000, 'Message is too long.'),
    serviceType: z.union([z.enum(SERVICE_TYPES), z.literal('')]).optional(),
    isNewCustomer: z.boolean().optional(),
    isJobApplication: z.boolean().optional(),
    agreeToPrivacy: z.boolean().optional(),
  })
  .superRefine((values, ctx) => {
    // Mirrors the handler's existing "service type required for service
    // requests" check, now validated up front instead of after parsing.
    if (!values.isJobApplication && !values.serviceType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['serviceType'],
        message: 'Service type is required for service requests.',
      });
    }
    if (!values.isJobApplication && values.agreeToPrivacy !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['agreeToPrivacy'],
        message: 'You must agree to the privacy policy.',
      });
    }
  });

export type ContactLeadInput = z.infer<typeof contactLeadSchema>;

/** Parsed shape of the validated m40_attr cookie. */
export type M40AttrCookie = z.infer<typeof m40AttrCookieSchema>;

/**
 * Maps a validated m40_attr cookie onto the CreateLeadInput shape createLead
 * expects. Shared across every consumer of m40AttrCookieSchema (POST
 * /api/leads today; the contact route in a separate PR, #284) so the
 * gbraid/wbraid routing — into the JSON attributionExtra column, since they
 * have no dedicated Lead column — lives in exactly one place. (WSP-834)
 */
export function mapAttrCookieToLeadInput(parsed: M40AttrCookie): Partial<CreateLeadInput> {
  const { gbraid, wbraid, ...firstClass } = parsed;
  const input: Partial<CreateLeadInput> = { ...firstClass };

  const extra: Record<string, unknown> = {};
  if (gbraid) extra.gbraid = gbraid;
  if (wbraid) extra.wbraid = wbraid;
  if (Object.keys(extra).length > 0) {
    input.attributionExtra = extra;
  }

  return input;
}

/**
 * Builds attribution from the edge: the validated m40_attr cookie (first-touch
 * ad/origin), the locale cookie, and the same-origin referer. Everything here
 * is best-effort — a missing or hostile source yields nothing rather than
 * failing the caller. Moved here (out of app/api/leads/route.ts) so every
 * Lead-creating route (POST /api/leads, POST /api/parts-requests, WSP-837)
 * shares the exact same attribution-capture logic. (WSP-834/WSP-837)
 */
export function extractAttribution(request: NextRequest): Partial<CreateLeadInput> {
  const attribution: Partial<CreateLeadInput> = {};

  // m40_attr cookie — validated, never cast: a malformed/hostile cookie is
  // coerced away, unknown keys are dropped, and each string is length-capped.
  const raw = request.cookies.get('m40_attr')?.value;
  if (raw) {
    try {
      const parsed = m40AttrCookieSchema.safeParse(JSON.parse(raw));
      if (parsed.success) {
        // gbraid/wbraid have no dedicated Lead column, so this routes them into
        // attributionExtra (the Lead.attribution JSON); everything else maps
        // 1:1 onto CreateLeadInput. Shared with every m40AttrCookieSchema
        // consumer so the routing logic lives in exactly one place. (WSP-834)
        Object.assign(attribution, mapAttrCookieToLeadInput(parsed.data));
      }
    } catch {
      // Non-JSON cookie — ignore.
    }
  }

  // Locale lives in the NEXT_LOCALE cookie in this app (there is no [locale] URL
  // segment), so read it where it actually is.
  const localeCookie = request.cookies.get(LOCALE_COOKIE_NAME)?.value;
  if (isSupportedLocale(localeCookie)) {
    attribution.locale = localeCookie;
  }

  // signupPath from the referer — but only when it is genuinely same-origin, so
  // a cross-origin caller can't poison the "which CTA converts" column.
  const referer = request.headers.get('referer');
  if (referer) {
    try {
      const url = new URL(referer);
      if (url.host === request.nextUrl.host) {
        attribution.signupPath = url.pathname;
      }
    } catch {
      // Unparseable referer — skip.
    }
  }

  return attribution;
}
